<template>
  <v-container v-if="loadView" :class="{'pa-2': $vuetify.breakpoint.xsOnly}" align="center">
    <v-form ref="form"
      @submit="checkForm"
      novalidate="true">
      <!-- BOOKING INFO -->
      <section-block :title="$t('sections.booking_info')">
        <template v-slot:aside>
          <h4 class="primary--text pt-2 font-weight-light">#{{ getUUIDNode(uuids.bookingList.bookingId) }}</h4>
        </template>
        <template v-slot:body>
          <v-row>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.mandatory.facility')"
                :items="facilities.list"
                item-text="name"
                item-value="id"
                v-model="facilityId"
                :error-messages="!facilityId && showErrors ? $t('errors.mandatory') : []">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.status')"
                :items="bookingTypes.list"
                item-text="name"
                item-value="id"
                v-model="bookingTypeId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :label="$t('form.mandatory.checkin_date')"
                :initialDate="reservation.initialCheckInDate"
                @change="v => { checkin = formatCheckin({ checkInDate: v, checkInTime: reservation.currentCheckInTime }); reservation.currentCheckInDate = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <time-input :label="$t('form.mandatory.checkin_time')"
                :initialTime="reservation.initialCheckInTime"
                @change="v => { checkin = formatCheckin({ checkInDate: reservation.currentCheckInDate, checkInTime: v }); reservation.currentCheckInTime = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :label="$t('form.checkout_date')"
                :initialDate="reservation.initialCheckOutDate"
                @change="v => { checkout = formatCheckout({ checkOutDate: v, checkOutTime: reservation.currentCheckOutTime }); reservation.currentCheckOutDate = v }"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <time-input :label="$t('form.checkout_time')"
                :initialTime="reservation.initialCheckOutTime"
                @change="v => { checkout = formatCheckout({ checkOutDate: reservation.currentCheckOutDate, checkOutTime: v }); reservation.currentCheckOutTime = v }"/>
            </v-col>
          </v-row>
        </template>
      </section-block>
      <!-- BOOKING PROFILE -->
      <section-block :title="$t('sections.booking_profile')"
        :subtitle="'#' + getUUIDNode(uuids.bookingList.profileId)"
        :otherOptions="true">
        <template v-slot:aside>
          <v-autocomplete class="mt-0 pt-0 aside-select"
            background-color="primary"
            solo dense flat dark hide-details
            :items="selectors.bookingProfileSelector"
            v-model="selectors.bookingProfileSelectorOption">
            <template v-slot:selection="{ item }">{{ $t('form.selector_values.' + item) }}</template>
            <template v-slot:item="{ item }">{{ $t('form.selector_values.' + item) }}</template>
          </v-autocomplete>
        </template>
        <template v-slot:body>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.mandatory.name')"
                v-model="name"
                :error-messages="!name && showErrors ? $t('errors.mandatory') : []"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.last_name')"
                v-model="lastName"/>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-text-field :label="$t('form.email')"
                v-model="email"/>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="3" class="py-0">
              <v-text-field :label="$t('form.phone_number')"
                v-model="phoneNumber"/>
            </v-col>
            <v-col cols="6" sm="3" class="py-0">
              <date-input :label="$t('form.birth_date')"
                :initialDate="profile.initialDateOfBirth"
                @change="v => dateOfBirth = formatDateOfBirth(v)"/>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <v-autocomplete :label="$t('form.language')"
                :items="languageCodes"
                item-text="name"
                item-value="code"
                v-model="languageCode">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-radio-group :label="$t('form.gender')" v-model="gender">
                <v-row>
                  <v-col>
                    <div class="d-inline-flex flex-wrap">
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="male"
                          :label="$t('form.genders.MALE')"
                          value="MALE"/>
                      </v-flex>
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="female"
                          :label="$t('form.genders.FEMALE')"
                          value="FEMALE"/>
                      </v-flex>
                      <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                        <v-radio key="other"
                          :label="$t('form.genders.OTHER')"
                          value="OTHER"/>
                      </v-flex>
                    </div>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
        </template>
        <template v-slot:otherOptions>
          <v-row>
            <v-col cols="6" class="py-0">
              <v-autocomplete :label="$t('form.profile_type')"
                :items="profileTypes.list"
                item-text="name"
                item-value="id"
                v-model="typeId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" class="py-0">
              <v-autocomplete :label="$t('form.profile_category')"
                :items="profileCategories.list"
                item-text="name"
                item-value="id"
                v-model="categoryId">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </template>
      </section-block>
      <!-- BOOKING ACCOUNT -->
      <section-block :title="$t('sections.booking_account')"
        :subtitle="'#' + getUUIDNode(uuids.bookingList.accountId)">
        <template v-slot:aside>
          <v-autocomplete class="mt-0 pt-0 aside-select"
            background-color="primary"
            solo dense flat dark hide-details
            :items="selectors.accountSelector"
            v-model="selectors.accountSelectorOption">
            <template v-slot:selection="{ item }">{{ $t('form.selector_values.' + item) }}</template>
            <template v-slot:item="{ item }">{{ $t('form.selector_values.' + item) }}</template>
          </v-autocomplete>
        </template>
        <template v-slot:body>
          <v-row v-for="(accountInfo, index) in currency.accountInfoList" :key="accountInfo.currencyId">
            <v-col cols="3" sm="6" class="py-0">
              <v-autocomplete :label="accountInfo.defaultValue ? $t('form.mandatory.default_currency') : $t('form.mandatory.currency')"
                :items="modifiedCurrencyList"
                item-text="name"
                item-value="id"
                v-model="accountInfo.currencyId"
                item-disabled="disabled"
                :disabled="selectors.accountSelectorOption !== selectorValues.NEW"
                @input="v => { accountInfo.balance = 0.0; handleCurrencyDisable(modifiedCurrencyList, currency.accountInfoList); removeDefaultCurrency(accountInfo) }">
                <template v-slot:selection="{ item }">{{ item.name }}</template>
                <template v-slot:item="{ item }">{{ item.name }}</template>
              </v-autocomplete>
            </v-col>
            <v-col cols="3" sm="2" class="py-0">
              <v-text-field :label="$t('form.balance')"
                type="number"
                step="0.01"
                min="0"
                v-model="accountInfo.balance"
                :disabled="accountInfo.postpaid || selectors.accountSelectorOption !== selectorValues.NEW"
                @input="removeDefaultCurrency(accountInfo)"/>
            </v-col>
            <v-col cols="3" sm="2" class="py-0">
              <v-checkbox :label="$t('form.postpaid')"
                v-model="accountInfo.postpaid"
                :disabled="selectors.accountSelectorOption !== selectorValues.NEW"
                @change="removeDefaultCurrency(accountInfo)"/>
            </v-col>
            <v-col cols="3" sm="2">
              <div class="rtl">
                <v-btn class="rounded-btn v-btn--icon white--text"
                  color="accent"
                  x-small depressed fab
                  :disabled="selectors.accountSelectorOption !== selectorValues.NEW"
                  @click="removeAccountInfo(currency.accountInfoList, index)">
                  <v-icon>close</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn class="rounded-btn v-btn--icon white--text"
                color="accent"
                x-small depressed fab
                :disabled="selectors.accountSelectorOption !== selectorValues.NEW"
                @click="addAccountInfo(currency.accountInfoList, {})">
                <v-icon>add</v-icon>
              </v-btn>
              <div class="d-inline px-4 text-uppercase monospaced"
                :class="{'grey--text': selectors.accountSelectorOption !== selectorValues.NEW, 'accent--text': selectors.accountSelectorOption === selectorValues.NEW}">
                {{ $t('sections.add_currency') }}
              </div>
            </v-col>
          </v-row>
        </template>
      </section-block>
      <!-- ROOM -->
      <expansion-section-block v-for="(roomInfo, roomIndex) in room.roomInfoList" :key="roomInfo.id"
        :title="$t('sections.room')"
        :subtitle="getRoomName(roomInfo.roomId) || $t('form.selector_values.NO_ROOM')"
        :footerAddon="true"
        @footerAddonClick="removeRoomInfo(roomIndex)">
        <template v-slot:body>
          <v-row>
            <v-col cols="6" class="pb-0">
              <v-autocomplete :label="$t('form.room')"
                :items="filteredRoomList"
                item-text="name"
                item-value="id"
                v-model="roomInfo.roomId"
                @change="v => { setGuestRooms(roomInfo, v); handleRoomDisable(); handleRoomAccountSelectorDisable(roomInfo) }">
                <template v-slot:selection="{ item }">
                  {{ item === selectorValues.NO_ROOM ? $t('form.selector_values.NO_ROOM') : item.name }}
                </template>
                <template v-slot:item="{ item }">
                  {{ item === selectorValues.NO_ROOM ? $t('form.selector_values.NO_ROOM') : item.name }}
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <!-- ROOM ACCOUNT -->
          <section-block :title="$t('sections.room_account')"
            :subtitle="'#' + getUUIDNode(roomInfo.roomAccountId)"
            :subsection="true"
            color="tertiary">
            <template v-slot:aside>
              <v-autocomplete class="mt-0 pt-0 aside-select"
                background-color="primary"
                solo dense flat dark hide-details
                :items="roomInfo.roomAccountSelector"
                v-model="roomInfo.roomAccountSelectorOption"
                :disabled="roomInfo.roomAccountSelectorDisabled">
                <template v-slot:selection="{ item }">{{ $t('form.selector_values.' + item) }}</template>
                <template v-slot:item="{ item }">{{ $t('form.selector_values.' + item) }}</template>
              </v-autocomplete>
            </template>
            <template v-slot:body>
              <v-row v-for="(roomAccountInfo, roomAccountIndex) in roomInfo.roomAccountInfoList" :key="roomAccountInfo.currencyId">
                <v-col cols="3" sm="6" class="py-0">
                  <v-autocomplete :label="$t('form.mandatory.currency')"
                    :items="roomInfo.modifiedRoomCurrencyList"
                    item-text="name"
                    item-value="id"
                    v-model="roomAccountInfo.currencyId"
                    item-disabled="disabled"
                    :disabled="roomInfo.roomAccountSelectorOption !== selectorValues.NEW ||
                      !roomInfo.roomId ||
                      roomInfo.roomId === selectorValues.NO_ROOM"
                    @input="v => {roomAccountInfo.balance = 0.0; handleCurrencyDisable(roomInfo.modifiedRoomCurrencyList, roomInfo.roomAccountInfoList)}">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="3" sm="2" class="py-0">
                  <v-text-field :label="$t('form.balance')"
                    type="number"
                    step="0.01"
                    min="0"
                    v-model="roomAccountInfo.balance"
                    :disabled="roomAccountInfo.postpaid ||
                      roomInfo.roomAccountSelectorOption !== selectorValues.NEW ||
                      !roomInfo.roomId ||
                      roomInfo.roomId === selectorValues.NO_ROOM"/>
                </v-col>
                <v-col cols="3" sm="2" class="py-0">
                  <v-checkbox :label="$t('form.postpaid')"
                    v-model="roomAccountInfo.postpaid"
                    :disabled="roomInfo.roomAccountSelectorOption !== selectorValues.NEW ||
                      !roomInfo.roomId ||
                      roomInfo.roomId === selectorValues.NO_ROOM"/>
                </v-col>
                <v-col cols="3" sm="2">
                  <div class="rtl">
                    <v-btn class="rounded-btn v-btn--icon white--text"
                      color="accent"
                      x-small depressed fab
                      :disabled="roomInfo.roomAccountSelectorOption !== selectorValues.NEW ||
                        !roomInfo.roomId ||
                        roomInfo.roomId === selectorValues.NO_ROOM"
                      @click="removeAccountInfo(roomInfo.roomAccountInfoList, roomAccountIndex); handleCurrencyDisable(roomInfo.modifiedRoomCurrencyList, roomInfo.roomAccountInfoList)">
                      <v-icon>close</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-btn class="rounded-btn v-btn--icon white--text"
                    color="accent"
                    x-small depressed fab
                    :disabled="roomInfo.roomAccountSelectorOption !== selectorValues.NEW ||
                      !roomInfo.roomId ||
                      roomInfo.roomId === selectorValues.NO_ROOM"
                    @click="addAccountInfo(roomInfo.roomAccountInfoList, {}); handleCurrencyDisable(roomInfo.modifiedRoomCurrencyList, roomInfo.roomAccountInfoList)">
                    <v-icon>add</v-icon>
                  </v-btn>
                  <div class="d-inline px-4 text-uppercase monospaced"
                    :class="{'grey--text': roomInfo.roomAccountSelectorOption !== selectorValues.NEW || !roomInfo.roomId || roomInfo.roomId === selectorValues.NO_ROOM,
                      'accent--text': roomInfo.roomAccountSelectorOption === selectorValues.NEW && roomInfo.roomId && roomInfo.roomId !== selectorValues.NO_ROOM}">
                    {{ $t('sections.add_currency') }}
                  </div>
                </v-col>
              </v-row>
            </template>
          </section-block>
          <!-- GUESTS -->
          <expansion-section-block v-for="(guestInfo, guestIndex) in roomInfo.guestInfoList" :key="guestInfo.id"
            :title="$t('sections.guest')"
            :subtitle="'#' + getUUIDNode(guestInfo.guest.id)"
            :otherOptions="true"
            otherOptionsColor="quaternary"
            :subsection="true"
            :footerAddon="roomInfo.guestInfoList.length > 1"
            @footerAddonClick="removeGuestInfo(roomInfo, guestIndex)"
            color="tertiary">
            <template v-slot:body>
              <v-row>
                <v-col cols="6" sm="3" class="py-0">
                  <v-text-field :label="$t('form.mandatory.name')"
                    v-model="guestInfo.profile.name"
                    :error-messages="!guestInfo.profile.name && showErrors ? $t('errors.mandatory') : []"/>
                </v-col>
                <v-col cols="6" sm="3" class="py-0">
                  <v-text-field :label="$t('form.last_name')"
                    v-model="guestInfo.profile.lastName"/>
                </v-col>
                <v-col cols="12" sm="6" class="py-0">
                  <v-text-field :label="$t('form.email')"
                    v-model="guestInfo.profile.email"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" sm="3" class="py-0">
                  <v-text-field :label="$t('form.phone_number')"
                    v-model="guestInfo.profile.phoneNumber"/>
                </v-col>
                <v-col cols="6" sm="3" class="py-0">
                  <date-input :label="$t('form.birth_date')"
                    @change="v => guestInfo.profile.dateOfBirth = formatDateOfBirth(v)"/>
                </v-col>
                <v-col cols="12" sm="6" class="py-0">
                  <v-autocomplete :label="$t('form.language')"
                    :items="languageCodes"
                    item-text="name"
                    item-value="code"
                    v-model="guestInfo.profile.languageCode">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="py-0">
                  <v-radio-group :label="$t('form.gender')" v-model="guestInfo.profile.gender">
                    <v-row>
                      <v-col>
                        <div class="d-inline-flex flex-wrap">
                          <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                            <v-radio key="male"
                              :label="$t('form.genders.MALE')"
                              value="MALE"/>
                          </v-flex>
                          <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                            <v-radio key="female"
                              :label="$t('form.genders.FEMALE')"
                              value="FEMALE"/>
                          </v-flex>
                          <v-flex class="pr-5" :class="{'py-2': $vuetify.breakpoint.xsOnly}">
                            <v-radio key="other"
                              :label="$t('form.genders.OTHER')"
                              value="OTHER"/>
                          </v-flex>
                        </div>
                      </v-col>
                    </v-row>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" sm="3" class="pb-0">
                  <date-input :label="$t('form.mandatory.checkin_date')"
                    :initialDate="guestInfo.initialCheckInDate"
                    @change="v => { guestInfo.guest.checkin = formatCheckin({ checkInDate: v, checkInTime: guestInfo.currentCheckInTime }); guestInfo.currentCheckInDate = v }"/>
                </v-col>
                <v-col cols="6" sm="3" class="pb-0">
                  <time-input :label="$t('form.mandatory.checkin_time')"
                    :initialTime="guestInfo.initialCheckInTime"
                    @change="v => { guestInfo.guest.checkin = formatCheckin({ checkInDate: guestInfo.currentCheckInDate, checkInTime: v }); guestInfo.currentCheckInTime = v }"/>
                </v-col>
                <v-col cols="6" sm="3" class="pb-0">
                  <date-input :label="$t('form.checkout_date')"
                    :initialDate="guestInfo.initialCheckOutDate"
                    @change="v => { guestInfo.guest.checkout = formatCheckout({ checkOutDate: v, checkOutTime: guestInfo.currentCheckOutTime }); guestInfo.currentCheckOutDate = v }"/>
                </v-col>
                <v-col cols="6" sm="3" class="pb-0">
                  <time-input :label="$t('form.checkout_time')"
                    :initialTime="guestInfo.initialCheckOutTime"
                    @change="v => { guestInfo.guest.checkout = formatCheckout({ checkOutDate: guestInfo.currentCheckOutDate, checkOutTime: v }); guestInfo.currentCheckOutTime = v }"/>
                </v-col>
              </v-row>
            </template>
            <template v-slot:otherOptions>
              <v-row>
                <v-col cols="6" sm="3" class="py-0">
                  <v-autocomplete :label="$t('form.profile_type')"
                    :items="profileTypes.list"
                    item-text="name"
                    item-value="id"
                    v-model="guestInfo.profile.typeId">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" sm="3" class="py-0">
                  <v-autocomplete :label="$t('form.profile_category')"
                    :items="profileCategories.list"
                    item-text="name"
                    item-value="id"
                    v-model="guestInfo.profile.categoryId">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" sm="3" class="py-0">
                  <v-autocomplete :label="$t('form.guest_type')"
                    :items="guestTypes.list"
                    item-text="name"
                    item-value="id"
                    v-model="guestInfo.guest.typeId">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" sm="3" class="py-0">
                  <v-autocomplete :label="$t('form.guest_category')"
                    :items="guestCategories.list"
                    item-text="name"
                    item-value="id"
                    v-model="guestInfo.guest.categoryId">
                    <template v-slot:selection="{ item }">{{ item.name }}</template>
                    <template v-slot:item="{ item }">{{ item.name }}</template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </template>
            <!-- GUEST ACCOUNT -->
            <template v-slot:extra>
              <section-block :title="$t('sections.guest_account')"
                :subtitle="'#' + getUUIDNode(guestInfo.guestAccountId)"
                :subsection="true"
                color="quaternary">
                <template v-slot:aside>
                  <v-autocomplete class="mt-0 pt-0 aside-select"
                    background-color="primary"
                    solo dense flat dark hide-details
                    :items="guestInfo.guestAccountSelector"
                    v-model="guestInfo.guestAccountSelectorOption">
                    <template v-slot:selection="{ item }">{{ $t('form.selector_values.' + item) }}</template>
                    <template v-slot:item="{ item }">{{ $t('form.selector_values.' + item) }}</template>
                  </v-autocomplete>
                </template>
                <template v-slot:body>
                  <v-row v-for="(guestAccountInfo, guestAccountIndex) in guestInfo.guestAccountInfoList" :key="guestAccountInfo.currencyId">
                    <v-col cols="3" sm="6" class="py-0">
                      <v-autocomplete :label="$t('form.mandatory.currency')"
                        :items="guestInfo.modifiedGuestCurrencyList"
                        item-text="name"
                        item-value="id"
                        v-model="guestAccountInfo.currencyId"
                        item-disabled="disabled"
                        :disabled="guestInfo.guestAccountSelectorOption !== selectorValues.NEW"
                        @input="v => {guestAccountInfo.balance = 0.0; handleCurrencyDisable(guestInfo.modifiedGuestCurrencyList, guestInfo.guestAccountInfoList)}">
                        <template v-slot:selection="{ item }">{{ item.name }}</template>
                        <template v-slot:item="{ item }">{{ item.name }}</template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="3" sm="2" class="py-0">
                      <v-text-field :label="$t('form.balance')"
                        type="number"
                        step="0.01"
                        min="0"
                        v-model="guestAccountInfo.balance"
                        :disabled="guestAccountInfo.postpaid || guestInfo.guestAccountSelectorOption !== selectorValues.NEW"/>
                    </v-col>
                    <v-col cols="3" sm="2" class="py-0">
                      <v-checkbox :label="$t('form.postpaid')"
                        v-model="guestAccountInfo.postpaid"
                        :disabled="guestInfo.guestAccountSelectorOption !== selectorValues.NEW"/>
                    </v-col>
                    <v-col cols="3" sm="2">
                      <div class="rtl">
                        <v-btn class="rounded-btn v-btn--icon white--text"
                          color="accent"
                          x-small depressed fab
                          :disabled="guestInfo.guestAccountSelectorOption !== selectorValues.NEW"
                          @click="removeAccountInfo(guestInfo.guestAccountInfoList, guestAccountIndex); handleCurrencyDisable(guestInfo.modifiedGuestCurrencyList, guestInfo.guestAccountInfoList)">
                          <v-icon>close</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-btn class="rounded-btn v-btn--icon white--text"
                        color="accent"
                        x-small depressed fab
                        :disabled="guestInfo.guestAccountSelectorOption !== selectorValues.NEW"
                        @click="addAccountInfo(guestInfo.guestAccountInfoList, {}); handleCurrencyDisable(guestInfo.modifiedGuestCurrencyList, guestInfo.guestAccountInfoList)">
                        <v-icon>add</v-icon>
                      </v-btn>
                      <div class="d-inline px-4 text-uppercase monospaced"
                        :class="{'grey--text': guestInfo.guestAccountSelectorOption !== selectorValues.NEW, 'accent--text': guestInfo.guestAccountSelectorOption === selectorValues.NEW}">
                        {{ $t('sections.add_currency') }}
                      </div>
                    </v-col>
                  </v-row>
                </template>
              </section-block>
            </template>
            <template v-slot:footerAddon>
              <v-col cols="12" align="center">
                <div class="d-inline px-4 text-uppercase white--text monospaced">
                  {{ $t('sections.delete_guest') }}
                </div>
              </v-col>
            </template>
          </expansion-section-block>
          <v-row>
            <v-col cols="12" class="pl-0">
              <v-btn class="rounded-btn v-btn--icon white--text"
                color="accent"
                x-small depressed fab
                @click="addGuestInfo(roomInfo)">
                <v-icon>add</v-icon>
              </v-btn>
              <div class="d-inline px-4 text-uppercase accent--text monospaced">
                {{ $t('sections.add_guest') }}
              </div>
            </v-col>
          </v-row>
        </template>
        <template v-slot:footerAddon>
          <v-col cols="12" align="center">
            <div class="d-inline px-4 text-uppercase white--text monospaced">
              {{ $t('sections.delete_room') }}
            </div>
          </v-col>
        </template>
      </expansion-section-block>
      <v-row>
        <v-col cols="12">
          <v-btn class="rounded-btn v-btn--icon white--text ml-7"
            color="accent"
            x-small depressed fab
            @click="addRoomInfo">
            <v-icon>add</v-icon>
          </v-btn>
          <div class="d-inline px-4 text-uppercase accent--text monospaced">
            {{ $t('sections.add_room') }}
          </div>
        </v-col>
      </v-row>
      <v-container class="px-5">
        <v-row>
          <v-col align="right">
            <v-btn color="accent"
              :loading="loading"
              type="submit">
              {{ $t('actions.create') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-container>
</template>

<script>
import moment from 'moment'
import { PANEL_LOCATION } from '@/config/environment'
import { mapState, mapGetters, mapMutations } from 'vuex'
import searchMixins from '@/mixins/Search.js'
import languageCodes from '@/config/languages'
import { Profile } from '@/entities/Profile'
import { Guest } from '@/entities/Guest'
import { selectorValues } from '@/config'
import { defaultValues } from '@/config'

export default {
  name: 'main-view',
  data () {
    return {
      loadView: false,
      loading: false,
      submiting: false,
      loadingDefaultData: false,
      showErrors: false,
      moment,
      languageCodes: languageCodes,
      selectorValues: selectorValues,
      reservation: {
        currentCheckInDate: null,
        currentCheckInTime: null,
        currentCheckOutDate: null,
        currentCheckOutTime: null,
        initialCheckInDate: null,
        initialCheckInTime: null,
        initialCheckOutDate: null,
        initialCheckOutTime: null
      },
      profile: {
        initialDateOfBirth: null
      },
      currency: {
        accountInfoList: []
      },
      selectors: {
        accountSelector: [selectorValues.NEW, selectorValues.NONE],
        accountSelectorOption: selectorValues.NEW,
        bookingProfileSelector: [selectorValues.NEW],
        bookingProfileSelectorOption: selectorValues.NEW
      },
      room: {
        roomInfoList: []
      }
    }
  },
  components: {
    SectionBlock: () => import('@/components/SectionBlock.vue'),
    ExpansionSectionBlock: () => import('@/components/ExpansionSectionBlock.vue'),
    DateInput: () => import('@/components/app-components/DateInput.vue'),
    TimeInput: () => import('@/components/app-components/TimeInput.vue')
  },
  mixins: [
    searchMixins
  ],
  async created () {
    Promise.all([
      this.$store.dispatch('facilities/list'),
      this.$store.dispatch('bookingTypes/list'),
      this.$store.dispatch('profileTypes/list'),
      this.$store.dispatch('profileCategories/list'),
      this.$store.dispatch('guestTypes/list'),
      this.$store.dispatch('guestCategories/list'),
      this.$store.dispatch('currencies/list'),
      this.$store.dispatch('rooms/list')
    ]).then(() => {
      this.$store.commit('uuids/generateRandomUUIDs')
      this.loadView = true
      this.setDefaultData()
    })
  },
  methods: {
    checkForm (e) {
      // Submit loading button
      this.loading = true
      // Prevent default event
      e.preventDefault()
      // Errors
      var errors = this.findErrors()
      // Submit
      if (!errors) {
        this.submit()
      } else {
        this.loading = false
        this.showErrors = true
        this.scrollToTop()
        this.submitMessage(this.$t('post.error_message'), 'error', 5000)
      }
    },
    findErrors () {
      if (!this.facilityId) {
        return true
      }
      if (!this.name) {
        return true
      }
      for (var roomInfo of this.room.roomInfoList) {
        for (var guestInfo of roomInfo.guestInfoList) {
          if (!guestInfo.profile.name) {
            return true
          }
        }
      }
      return false
    },
    async submit () {
      this.submiting = true
      // Profile
      var profileId = await this.submitProfile()
      console.log('SUCCESS - profileId: ', profileId)
      // Account
      var accountId = null
      if (this.selectors.accountSelectorOption === selectorValues.NEW) {
        accountId = await this.submitAccount(this.uuids.bookingList.accountId, profileId)
        console.log('SUCCESS - accountId: ', accountId)
        await this.submitCurrencies(this.currency.accountInfoList, accountId)
      }
      // Booking
      var bookingId = await this.submitBooking(profileId, accountId)
      console.log('SUCCESS - bookingId: ', bookingId)
      // Rooms
      for (var roomInfo of this.room.roomInfoList) {
        // Guests
        for (var guestInfo of roomInfo.guestInfoList) {
          // Guest profile
          var guestProfileId = await this.submitGuestProfile(guestInfo.profile)
          console.log('SUCCESS - guestProfileId: ', guestProfileId)
          // Guest
          var guestAccountId = null
          if (guestInfo.guestAccountSelectorOption === selectorValues.NEW) {
            guestAccountId = await this.submitAccount(guestInfo.guestAccountId, guestProfileId)
            console.log('SUCCESS - guestAccountId: ', guestAccountId)
            await this.submitCurrencies(guestInfo.guestAccountInfoList, guestAccountId)
          }
          var guestId = await this.submitGuest(guestProfileId, guestInfo.guest, bookingId, guestAccountId)
          console.log('SUCCESS - guestId: ', guestId)
        }
        // Room account
        var roomAccountId = null
        if (roomInfo.roomAccountSelectorOption === selectorValues.NEW &&
            roomInfo.roomId &&
            roomInfo.roomId !== selectorValues.NO_ROOM &&
            roomInfo.guestInfoList.length > 0) {
          roomAccountId = await this.submitAccount(roomInfo.roomAccountId, profileId)
          this.submitRoomAccount(bookingId, roomInfo.roomId, roomAccountId)
          console.log('SUCCESS - roomAccountId: ', roomAccountId)
          await this.submitCurrencies(roomInfo.roomAccountInfoList, roomAccountId)
        }
      }
      this.submitMessage(this.$t('post.success_message'), 'success', 5000)
      this.closeWindow()
    },
    async submitProfile () {
      return await this.$store.dispatch('profiles/save', { id: this.uuids.bookingList.profileId })
    },
    async submitAccount (id, profileId) {
      this.$store.commit('accounts/setField', { field: 'facilityId', value: this.facilityId })
      return await this.$store.dispatch('accounts/save', { id: id, profileId: profileId, active: true })
    },
    async submitRoomAccount (bookingId, roomId, accountId) {
      this.$store.dispatch('bookings/roomAccount', { bookingId: bookingId, roomId: roomId, accountId: accountId })
    },
    async submitCurrencies (accountInfoList, accountId) {
      accountInfoList.forEach(async accountInfo => {
        if (accountInfo.currencyId) {
          if (accountInfo.postpaid) {
            this.submitPostpaid(accountId, accountInfo.currencyId)
            console.log('SUCCESS - postpaid')
          } else {
            if (accountInfo.balance > 0) {
              var transactionId = await this.submitTransaction(accountId, accountInfo.currencyId, accountInfo.balance)
              console.log('SUCCESS - transactionId: ', transactionId)
            }
          }
        }
      })
    },
    async submitPostpaid (accountId, currencyId) {
      this.$store.dispatch('currencies/postpaid', { accountId: accountId, currencyId: currencyId })
    },
    async submitTransaction (accountId, currencyId, balance) {
      return await this.$store.dispatch('transactions/save', { accountId: accountId, currencyId: currencyId, amount: parseFloat(balance), type: 'PAYMENT' })
    },
    async submitBooking (profileId, accountId) {
      return await this.$store.dispatch('bookings/save', { id: this.uuids.bookingList.bookingId, profileId: profileId, accountId: accountId })
    },
    async submitGuestProfile (guestProfile) {
      this.$store.commit('profiles/resetEdited')
      this.$store.commit('profiles/setRead', guestProfile)
      return await this.$store.dispatch('profiles/save', {})
    },
    async submitGuest (guestProfileId, guest, bookingId, guestAccountId) {
      this.$store.commit('guests/resetEdited')
      this.$store.commit('guests/setRead', guest)
      if (guestAccountId) {
        this.$store.commit('guests/setField', { field: 'accountId', value: guestAccountId })
      }
      return await this.$store.dispatch('guests/save', { profileId: guestProfileId, bookingId: bookingId, active: true })
    },
    submitMessage (text, color, timeout) {
      this.$store.commit('state/setMessage', { text: this.$t('post.success_message'), color: 'success', timeout: 5000 })
      this.$store.commit('state/setMessage', { text: text, color: color, timeout: timeout })
    },
    closeWindow () {
      if (window.opener) {
        window.opener.postMessage('refreshData', PANEL_LOCATION)
        window.close()
      }
    },
    formatDateOfBirth (value) {
      return moment(value).add(12,'h').format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
    },
    formatCheckin ({ checkInDate, checkInTime }) {
      if (checkInDate && checkInTime) {
        return moment(checkInDate + 'T' + checkInTime).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
      }
    },
    formatCheckout ({ checkOutDate, checkOutTime }) {
      if (checkOutDate && checkOutTime) {
        return moment(checkOutDate + 'T' + checkOutTime).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
      }
    },
    addAccountInfo (list, { currencyId, postpaid, balance, defaultValue }) {
      list.push({
        currencyId: currencyId || null,
        postpaid: postpaid || false,
        balance: balance || null,
        defaultValue: defaultValue || false
      })
    },
    removeAccountInfo (list, index) {
      list.splice(index, 1)
    },
    handleCurrencyDisable (currencyList, accountInfoList) {
      currencyList.map(currency => {
        if (accountInfoList.find(accountInfo => accountInfo.currencyId === currency.id)) {
          currency['disabled'] = true
          return
        }
        currency['disabled'] = false
      })
    },
    removeDefaultCurrency (accountInfo) {
      if (accountInfo.defaultValue && !this.loadingDefaultData) {
        accountInfo['defaultValue'] = false
      }
    },
    resetDefaultCurrency () {
      var accountInfo = this.currency.accountInfoList.find(accountInfo => accountInfo.defaultValue)
      if (accountInfo) {
        accountInfo.currencyId = this.getBaseCurrencyId(this.facilityId)
      }
    },
    addRoomInfo ({ roomId }) {
      var roomInfo = {
        id: this.getRandomUUID(),
        roomId: roomId || selectorValues.NO_ROOM,
        modifiedRoomCurrencyList: JSON.parse(JSON.stringify(this.currencies.modifiedList)),
        roomAccountId: this.getRandomUUID(),
        roomAccountInfoList: [],
        roomAccountSelector: [selectorValues.NEW, selectorValues.NONE],
        roomAccountSelectorOption: selectorValues.NONE,
        roomAccountSelectorDisabled: roomId ? false : true,
        guestInfoList: []
      }
      this.addGuestInfo(roomInfo)
      this.room.roomInfoList.push(roomInfo)
      return roomInfo
    },
    removeRoomInfo (index) {
      this.room.roomInfoList.splice(index, 1)
    },
    handleRoomDisable () {
      this.filteredRoomList.map(room => {
        if (room !== selectorValues.NO_ROOM) {
          if (this.room.roomInfoList.find(roomInfo => roomInfo.roomId === room.id)) {
            room['disabled'] = true
            return
          }
          room['disabled'] = false
        }
      })
    },
    handleRoomAccountSelectorDisable (roomInfo) {
      if (roomInfo.roomId && roomInfo.roomId !== selectorValues.NO_ROOM) {
        roomInfo.roomAccountSelectorDisabled = false
      } else {
        roomInfo.roomAccountSelectorDisabled = true
        roomInfo.roomAccountSelectorOption = selectorValues.NONE
      }
    },
    addGuestInfo (roomInfo) {
      // -- Random guest data when adding guests
      var randomGuestData = this.randomGuestData(roomInfo)
      var profile = randomGuestData.guestProfile
      var guest = randomGuestData.guest
      // --
      roomInfo.guestInfoList.push({
        id: this.getRandomUUID(),
        profile: profile || new Profile(),
        guest: guest || new Guest({
          id: this.getRandomUUID(),
          roomId: (roomInfo.roomId === selectorValues.NO_ROOM ? null : roomInfo.roomId) || null
        }),
        currentCheckInDate: null,
        currentCheckInTime: null,
        currentCheckOutDate: null,
        currentCheckOutTime: null,
        initialCheckInDate: this.reservation.currentCheckInDate || this.reservation.initialCheckInDate || null,
        initialCheckInTime: this.reservation.currentCheckInTime || this.reservation.initialCheckInTime || null,
        initialCheckOutDate: this.reservation.currentCheckOutDate || this.reservation.initialCheckOutDate || null,
        initialCheckOutTime: this.reservation.currentCheckOutTime || this.reservation.initialCheckOutTime || null,
        modifiedGuestCurrencyList: JSON.parse(JSON.stringify(this.currencies.modifiedList)),
        guestAccountId: this.getRandomUUID(),
        guestAccountInfoList: [],
        guestAccountSelector: [selectorValues.NEW, selectorValues.NONE],
        guestAccountSelectorOption: selectorValues.NONE
      })
    },
    removeGuestInfo (roomInfo, index) {
      roomInfo.guestInfoList.splice(index, 1)
    },
    setGuestRooms (roomInfo, roomId) {
      roomInfo.guestInfoList.map(guestInfo => {
        roomId === selectorValues.NO_ROOM ? guestInfo.guest['roomId'] = null : guestInfo.guest['roomId'] = roomId
      })
    },
    resetRooms () {
      this.room.roomInfoList.map(roomInfo => {
        if (!this.filteredRoomList.some(room => room.id === roomInfo.roomId)) {
          roomInfo.roomId = selectorValues.NO_ROOM
          roomInfo.roomAccountSelectorOption = selectorValues.NONE
          roomInfo.roomAccountSelectorDisabled = true
        }
      })
    },
    getRandomItem (list) {
      return list[Math.floor(Math.random() * list.length)]
    },
    scrollToTop () {
      this.$refs.form.$el.scrollIntoView({ behavior: 'smooth' })
    },
    randomGuestData (roomInfo) {
      var guestProfileData = this.getRandomProfileData()
      var guestProfile = new Profile({
        name: guestProfileData.name,
        lastName: guestProfileData.lastName,
        email: guestProfileData.email,
        phoneNumber: guestProfileData.phoneNumber,
        dateOfBirth: guestProfileData.dateOfBirth,
        languageCode: this.getRandomItem(this.languageCodes).code,
        gender: guestProfileData.gender
      })
      var guest = new Guest({
        id: this.getRandomUUID(),
        roomId: roomInfo.roomId === selectorValues.NO_ROOM ? null : roomInfo.roomId
      })
      return { guestProfile, guest }
    },
    setDefaultData () {
      this.loadingDefaultData = true
      // Booking data
      this.checkFacilityId(this.$store.getters['state/facilityId']) ?
        this.facilityId = this.$store.getters['state/facilityId'] : this.facilityId = this.getFirstFacilityId()
      this.reservation.initialCheckInDate = defaultValues.CHECKIN_DATE
      this.reservation.initialCheckInTime = defaultValues.CHECKIN_TIME
      this.reservation.initialCheckOutDate = defaultValues.CHECKOUT_DATE
      this.reservation.initialCheckOutTime = defaultValues.CHECKOUT_TIME
      // Booking profile
      var bookingProfileData = this.getRandomProfileData()
      this.name = bookingProfileData.name
      this.lastName = bookingProfileData.lastName
      this.email = bookingProfileData.email
      this.phoneNumber = bookingProfileData.phoneNumber
      this.profile.initialDateOfBirth = bookingProfileData.dateOfBirth
      this.languageCode = this.getRandomItem(this.languageCodes).code
      this.gender = bookingProfileData.gender
      // Booking account
      this.addAccountInfo(this.currency.accountInfoList, { currencyId: this.getBaseCurrencyId(this.facilityId), postpaid: true, balance: '0', defaultValue: true })
      // Guests
      this.$store.commit('rooms/filteredRoomList', this.facilityId)
      for (var i = 0; i < defaultValues.NUMBER_OF_ROOMS; i++) {
        this.addRoomInfo({ roomId: this.getFirstFilteredRoomId(this.room.roomInfoList) })
        var roomInfo = this.room.roomInfoList[this.room.roomInfoList.length - 1]
        for (var j = 1; j < defaultValues.NUMBER_OF_GUESTS; j++) {
          this.addGuestInfo(roomInfo)
        }
      }
      this.loadingDefaultData = false
    }
  },
  computed: {
    ...mapState(['state', 'facilities', 'bookingTypes', 'profiles', 'bookings', 'profileTypes', 'profileCategories', 'currencies', 'accounts', 'transactions', 'rooms', 'guestTypes', 'guestCategories', 'guests', 'uuids']),
    ...mapGetters('profiles', ['getRandomProfileData']),
    ...mapGetters('facilities', ['checkFacilityId', 'getBaseCurrencyId', 'getFirstFacilityId']),
    ...mapGetters('uuids', ['getRandomUUID', 'getUUIDNode']),
    ...mapGetters('rooms', ['getRoomName', 'getFirstFilteredRoomId']),
    ...mapMutations('uuids', ['generateRandomUUIDs']),
    facilityId: {
      get () {
        return this.bookings.edited ? this.bookings.edited.facilityId : null
      },
      set (value) {
        this.$store.commit('accounts/setField', { field: 'facilityId', value })
        this.$store.commit('bookings/setField', { field: 'facilityId', value })
      }
    },
    bookingTypeId: {
      get () {
        return this.bookings.edited ? this.bookings.edited.typeId : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'typeId', value })
      }
    },
    checkin: {
      get () {
        return this.bookings.edited ? this.bookings.edited.checkin : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'checkin', value })
      }
    },
    checkout: {
      get () {
        return this.bookings.edited ? this.bookings.edited.checkout : null
      },
      set (value) {
        this.$store.commit('bookings/setField', { field: 'checkout', value })
      }
    },
    name: {
      get () {
        return this.profiles.edited ? this.profiles.edited.name : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'name', value })
      }
    },
    lastName: {
      get () {
        return this.profiles.edited ? this.profiles.edited.lastName : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'lastName', value })
      }
    },
    email: {
      get () {
        return this.profiles.edited ? this.profiles.edited.email : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'email', value })
      }
    },
    phoneNumber: {
      get () {
        return this.profiles.edited ? this.profiles.edited.phoneNumber : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'phoneNumber', value })
      }
    },
    dateOfBirth: {
      get () {
        return this.profiles.edited ? this.profiles.edited.dateOfBirth : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'dateOfBirth', value })
      }
    },
    gender: {
      get () {
        return this.profiles.edited ? this.profiles.edited.gender : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'gender', value })
      }
    },
    languageCode: {
      get () {
        return this.profiles.edited ? this.profiles.edited.languageCode : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'languageCode', value })
      }
    },
    typeId: {
      get () {
        return this.profiles.edited ? this.profiles.edited.typeId : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'typeId', value })
      }
    },
    categoryId: {
      get () {
        return this.profiles.edited ? this.profiles.edited.categoryId : null
      },
      set (value) {
        this.$store.commit('profiles/setField', { field: 'categoryId', value })
      }
    },
    modifiedCurrencyList: {
      get () {
        return this.currencies.modifiedCurrencyList || null
      },
      set (value) {
        this.$store.commit('currencies/setModifiedCurrencyList', value)
      }
    },
    modifiedRoomList: {
      get () {
        return this.rooms.modifiedRoomList || null
      },
      set (value) {
        this.$store.commit('rooms/setModifiedRoomList', value)
      }
    },
    filteredRoomList: {
      get () {
        return this.rooms.filteredRoomList || null
      },
      set (value) {
        this.$store.commit('rooms/setFilteredRoomList', value)
      }
    }
  },
  watch: {
    facilityId: {
      handler (newValue) {
        this.$store.commit('rooms/filteredRoomList', newValue)
        if (!this.submiting) {
          this.resetRooms()
          this.resetDefaultCurrency()
        }
      }
    },
    'currency.accountInfoList': {
      handler () {
        this.handleCurrencyDisable(this.modifiedCurrencyList, this.currency.accountInfoList)
      }
    },
    'room.roomInfoList': {
      handler () {
        this.handleRoomDisable()
      }
    }
  }
}
</script>

<style lang="scss">
.aside-select {
  border-radius: 10px;
  label.v-label {
    color: white !important;
  }
}
.rounded-btn {
  border-radius: 28px !important;
}
</style>
